import React, { ChangeEvent } from 'react';
import log from '../../../util/log';
import { MAX_IMAGE_WIDTH, resamplePhoto, resizeMax } from '../imageHelpers/imageHelpers';
import { useImageUpload } from '../ImageUploadContext/ImageUploadContext';

const ImageFileUpload = () => {
  const { handleFileUploadComplete, setIsFileLoading, fileInputRef, onError } = useImageUpload();
  const getPhoto = (evt: ChangeEvent<HTMLInputElement>) => {
    let error: string | null;
    const fileToUpload = evt.target.files && evt.target.files[0];

    if (!fileToUpload) {
      // file dialog cancelled
      handleFileUploadComplete();
    }
    if (fileToUpload && fileToUpload.size > 85400000 && /Firefox/.test(navigator.userAgent)) {
      error = 'File too large, please try a smaller size.';
    } else if (
      fileToUpload &&
      !['image/png', 'image/jpeg', 'image/jpg'].includes(fileToUpload.type)
    ) {
      error = 'Please upload a PNG or JPG image.';
    } else {
      error = null;

      // Read image as DataURL
      const reader = new FileReader();
      reader.addEventListener('load', (event) => {
        setIsFileLoading(true);
        const can = document.createElement('canvas');
        const ctx = can.getContext('2d');
        const img = new Image();
        if (onError) {
          img.addEventListener('error', onError);
        }
        img.addEventListener('load', () => {
          if (fileInputRef?.current) {
            // clear the value to allow onchange to detect the same file again
            fileInputRef.current.value = '';
          }
          let w = img.width;
          let h = img.height;

          // Fix for maximum canvas size error on iOS.
          if (w * h > 16000000) {
            const scaleDown = 16000000 / (w * h);
            w = w * scaleDown;
            h = h * scaleDown;
          }

          if (ctx) {
            ctx.canvas.width = w;
            ctx.canvas.height = h;
            ctx.drawImage(img, 0, 0, w, h);
          }

          const maxed = MAX_IMAGE_WIDTH;
          [w, h] = resizeMax({ width: w, height: h, maxWidth: maxed });

          resamplePhoto({ canvas: can, widthProp: w, heightProp: h, resizeCanvas: true });
          handleFileUploadComplete(can.toDataURL());
        });
        try {
          img.src = event.target?.result as string;
        } catch {
          error = 'File too large, please try a smaller size.';
        }
        if (error) {
          log.error('Failed on load image event lister on file upload:', error);
        }
      });
      reader.readAsDataURL(fileToUpload as Blob);
      if (error) {
        log.error('Failed on reading file on file upload:', error);
      }
    }
  };

  return (
    <input
      style={{ display: 'none' }}
      id="fileInputRef"
      className="add-a-pet__file-input"
      type="file"
      title="Upload Photo"
      onChange={getPhoto}
      accept="image/png,image/jpeg,image/jpg"
      data-testid="add-a-pet__file-input"
      ref={fileInputRef}
    />
  );
};

export default ImageFileUpload;
